import { useEffect, useRef, useState } from "react";
import { supabase, SUPABASE_PUBLISHABLE_KEY, SUPABASE_URL } from "../lib/supabase";
import { myId } from "../lib/me";
import { Workspace } from "../components/Shell";
import { SettingsHead } from "../components/AccountTabs";
import {
  clientMatches, dispositionName, exportRequest, failureText, libraryLine, sizeLabel,
  type ExportKind, type ExportShape, type LibraryFileCounts, type PickableClient,
} from "../lib/yourData";

/* W72 STEP 8 — YOUR DATA: take your work with you (Proposal 8, his yes 2026-09-25 ~00:45).
 *
 * Two rows on the ground and the sentence under them. Each download is built by the `export-data` edge function,
 * which reads AS THIS COACH (their own session, RLS deciding every row — no service key, no new policy), and comes
 * back as a zip with a README naming every field, unit and the time zone. It downloads in the browser now; it will be
 * emailed once the invitation's email sender (F13) exists, and the header says so rather than promising an email.
 *
 * The request LOG (`data_exports`, pushed 2026-09-26 on his "go on all") is written by the `export-data` function AS
 * the coach once the archive is built; this page does not read it yet, so it claims nothing about it. */

type Status = { kind: "idle" } | { kind: "busy"; what: string } | { kind: "done"; text: string } | { kind: "failed"; text: string };

export function YourData({ foreground = true }: { foreground?: boolean }) {
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => { if (foreground) heading.current?.focus(); }, [foreground]);

  const [counts, setCounts] = useState<LibraryFileCounts>({ exercises: null, workouts: null, programs: null, equipment: null });
  const [clients, setClients] = useState<PickableClient[] | null>(null);
  const [query, setQuery] = useState("");
  const [chosen, setChosen] = useState<PickableClient | null>(null);
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const me = await myId();
      // Counted the way the FILE is built — every row the coach owns, hidden and archived included.
      const n = async (q: PromiseLike<{ count: number | null; error: unknown }>) => { const r = await q; return r.error === null ? r.count : null; };
      const [exercises, workouts, programs, equipment] = await Promise.all([
        n(supabase.from("coach_exercises").select("id", { count: "exact", head: true }).eq("coach_id", me)),
        n(supabase.from("coach_workouts").select("id", { count: "exact", head: true }).eq("coach_id", me).is("client_id", null)),
        n(supabase.from("coach_programs").select("id", { count: "exact", head: true }).eq("coach_id", me)),
        n(supabase.from("coach_equipment").select("id", { count: "exact", head: true }).eq("coach_id", me)),
      ]);
      if (!cancelled) setCounts({ exercises, workouts, programs, equipment });
      const { data: links } = await supabase.from("coach_clients")
        .select("client_id, invited_first_name, invited_last_name")
        .eq("coach_id", me).eq("status", "active").not("client_id", "is", null);
      const ids = (links ?? []).map((l) => l.client_id).filter((x): x is string => typeof x === "string");
      const names = new Map<string, string>();
      if (ids.length > 0) {
        const { data: people } = await supabase.from("profiles").select("id, first_name, last_name, nickname").in("id", ids);
        for (const p of people ?? []) {
          const full = [p.first_name, p.last_name].filter(Boolean).join(" ").trim();
          names.set(p.id, full !== "" ? full : (p.nickname ?? "").trim());
        }
      }
      if (cancelled) return;
      setClients((links ?? []).filter((l) => typeof l.client_id === "string").map((l) => {
        const invited = [l.invited_first_name, l.invited_last_name].filter(Boolean).join(" ").trim();
        return { id: l.client_id as string, name: names.get(l.client_id as string) || invited || "Unnamed client" };
      }));
    })();
    return () => { cancelled = true; };
  }, []);

  async function download(kind: ExportKind, shape: ExportShape) {
    const what = kind === "library" ? `your library as ${shape.toUpperCase()}` : `${chosen?.name ?? "the client"}'s history as ${shape.toUpperCase()}`;
    setStatus({ kind: "busy", what });
    try {
      const body = exportRequest(kind, shape, chosen?.id ?? null);
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (token === undefined) { setStatus({ kind: "failed", text: "You are signed out." }); return; }
      const res = await fetch(`${SUPABASE_URL}/functions/v1/export-data`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, apikey: SUPABASE_PUBLISHABLE_KEY, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const answer = await res.json().catch(() => null) as { error?: unknown; message?: unknown } | null;
        setStatus({ kind: "failed", text: failureText(res.status, answer) });
        return;
      }
      const blob = await res.blob();
      const name = dispositionName(res.headers.get("Content-Disposition"), `coachwork-${kind}-${shape}.zip`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      setStatus({ kind: "done", text: `Saved ${name} (${sizeLabel(blob.size)}).` });
    } catch (e) {
      setStatus({ kind: "failed", text: e instanceof Error ? e.message : "The download failed." });
    }
  }

  const busy = status.kind === "busy";
  const matches = clients === null ? [] : clientMatches(clients, query).slice(0, 6);

  return (
    <Workspace>
      <main id="main" className="account-page yourdata-page ours-page">
        <SettingsHead current="data" headingRef={heading} />
        <p className="ht-caption yourdata-lede">Each download is built when you ask and saved by your browser — email delivery comes later.</p>

        <h2 className="yourdata-overline">Take your work with you</h2>
        <ul className="yourdata-rows">
          <li className="yourdata-row">
            <div className="yourdata-what">
              <p className="yourdata-title">Your library</p>
              <p className="yourdata-sub">{libraryLine(counts)}</p>
            </div>
            <div className="yourdata-actions">
              <button type="button" className="ht-button" disabled={busy} onClick={() => { void download("library", "json"); }}>Download · JSON</button>
              <button type="button" className="ht-button" disabled={busy} onClick={() => { void download("library", "csv"); }}>Download · CSV</button>
            </div>
          </li>
          <li className="yourdata-row">
            <div className="yourdata-what">
              <p className="yourdata-title">A client's history</p>
              <p className="yourdata-sub">Every logged set, weigh-in and message with that client, from the day they joined — never their clips or photos</p>
              {chosen === null ? (
                <div className="yourdata-pick">
                  <label className="ht-visually-hidden" htmlFor="yourdata-client">Choose a client</label>
                  <input id="yourdata-client" className="ht-input" type="search" placeholder="Choose a client…" autoComplete="off"
                         value={query} onChange={(e) => { setQuery(e.target.value); }} />
                  {clients !== null && clients.length === 0 && <p className="ht-caption">No active clients yet.</p>}
                  {query.trim() !== "" && matches.length === 0 && clients !== null && clients.length > 0 &&
                    <p className="ht-caption">Nobody by that name.</p>}
                  {matches.length > 0 && (query.trim() !== "" || (clients?.length ?? 0) <= 6) && (
                    <ul className="yourdata-matches">
                      {matches.map((c) => (
                        <li key={c.id}><button type="button" className="yourdata-match" onClick={() => { setChosen(c); setQuery(""); }}>{c.name}</button></li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : (
                <p className="yourdata-chosen">
                  <span className="yourdata-chosen-name">{chosen.name}</span>
                  <button type="button" className="ht-link yourdata-change" onClick={() => { setChosen(null); }}>Change</button>
                </p>
              )}
            </div>
            <div className="yourdata-actions">
              <button type="button" className="ht-button" disabled={busy || chosen === null} onClick={() => { void download("client-history", "json"); }}>Download · JSON</button>
              <button type="button" className="ht-button" disabled={busy || chosen === null} onClick={() => { void download("client-history", "csv"); }}>Download · CSV</button>
            </div>
          </li>
        </ul>

        <p className="yourdata-status" role="status" aria-live="polite">
          {status.kind === "busy" ? `Building ${status.what}…` : status.kind === "done" ? status.text : ""}
        </p>
        {status.kind === "failed" && <p className="ht-callout yourdata-error" role="alert">{status.text}</p>}

        <p className="yourdata-promise">
          Every file is a zip with a README naming each field, its unit and the time zone. Nothing here is a subscription: it is yours whether you stay or go.
        </p>
      </main>
    </Workspace>
  );
}
